import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { ArrowRight, Building2, CalendarRange, CircleDollarSign, Globe, RefreshCw, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { BreakdownRow } from "@/api/generated/BreakdownRow"
import { useAgents } from "@/api/queries/agents"
import {
  BREAKDOWN_DIMENSIONS,
  type BreakdownDimension,
  INSIGHTS_PERIODS,
  type InsightsFilters,
  type InsightsPeriod,
  insightsKeys,
  PLATFORM_WINDOWS,
  type PlatformWindow,
} from "@/api/queries/insights"
import { useModels } from "@/api/queries/providers"
import { RUN_SOURCES } from "@/api/queries/runs"
import { useSession } from "@/api/queries/session"
import { PageContainer, PageHeader } from "@/components/app/page"
import { EmptyState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PlatformStatsView } from "@/features/insights/platform-stats"
import { endOfDay, isValidRange } from "@/features/insights/series"
import { TenantCosts } from "@/features/insights/tenant-costs"
import { useSourceLabel } from "@/features/runs/run-status"
import { can } from "@/lib/permissions"

interface CostsSearch {
  period?: InsightsPeriod
  /** custom range, "YYYY-MM-DD" (inclusive) */
  from?: string
  to?: string
  agent?: number
  model?: number
  user?: number
  source?: string
  dim?: BreakdownDimension
  /** `platform`: cross-tenant statistics (platform admins). */
  view?: "platform"
  window?: PlatformWindow
}

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined)
const num = (v: unknown) => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined
}
const day = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined)
const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined =>
  list.includes(v as T) ? (v as T) : undefined

export const Route = createFileRoute("/_app/insights/costs")({
  staticData: { titleKey: "nav.items.costs" },
  validateSearch: (s: Record<string, unknown>): CostsSearch => ({
    period: oneOf(INSIGHTS_PERIODS, s.period),
    from: day(s.from),
    to: day(s.to),
    agent: num(s.agent),
    model: num(s.model),
    user: num(s.user),
    source: str(s.source),
    dim: oneOf(BREAKDOWN_DIMENSIONS, s.dim),
    view: s.view === "platform" ? "platform" : undefined,
    window: oneOf(PLATFORM_WINDOWS, s.window),
  }),
  component: CostsPage,
})

/** Labels of users picked from the breakdown (the URL only carries the id). */
const userLabels = new Map<number, string>()

function CostsPage() {
  const { t } = useTranslation()
  const session = useSession()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const qc = useQueryClient()
  const sourceLabel = useSourceLabel()
  const agents = useAgents({ include_archived: true })
  const models = useModels()
  const isPlatformAdmin = can(session, "platformAdmin")
  const view = search.view === "platform" && isPlatformAdmin ? "platform" : "tenant"

  const setSearch = (patch: Partial<CostsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const period = search.period ?? "7d"
  const customValid = period !== "custom" || isValidRange(search.from, search.to)
  const filters: InsightsFilters = {
    period,
    start: period === "custom" ? search.from : undefined,
    end: period === "custom" && search.to ? endOfDay(search.to) : undefined,
    agent_id: search.agent,
    model_id: search.model,
    user_id: search.user,
    source: search.source,
  }
  const dimension = search.dim ?? "agent"
  const hasFilters = !!(search.agent || search.model || search.user || search.source)

  const drill = (dim: BreakdownDimension, row: BreakdownRow) => {
    if (dim === "source") return setSearch({ source: row.key || undefined })
    if (row.id == null) return
    if (dim === "agent") setSearch({ agent: row.id, dim: "model" })
    else if (dim === "model") setSearch({ model: row.id })
    else {
      userLabels.set(row.id, row.label)
      setSearch({ user: row.id })
    }
  }

  const agentName = (id: number) => agents.data?.find((a) => a.id === id)?.name ?? `#${id}`
  const modelName = (id: number) => models.data?.find((m) => m.id === id)?.name ?? `#${id}`

  return (
    <PageContainer width="full" className="max-w-[100rem]">
      <PageHeader
        title={t("insights.costs.title")}
        description={t("insights.costs.description")}
        icon={CircleDollarSign}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void qc.invalidateQueries({ queryKey: insightsKeys.all })}
          >
            <RefreshCw />
            {t("insights.costs.refresh")}
          </Button>
        }
      >
        {isPlatformAdmin && (
          <Tabs
            value={view}
            onValueChange={(v) => setSearch({ view: v === "platform" ? "platform" : undefined })}
          >
            <TabsList variant="line" className="h-8">
              <TabsTrigger value="tenant" className="px-2">
                <Building2 />
                {t("insights.costs.views.tenant")}
              </TabsTrigger>
              <TabsTrigger value="platform" className="px-2">
                <Globe />
                {t("insights.costs.views.platform")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </PageHeader>

      {view === "platform" ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={search.window ?? "24h"}
              onValueChange={(v) =>
                setSearch({ window: v === "24h" || v == null ? undefined : (v as PlatformWindow) })
              }
            >
              <SelectTrigger size="sm" className="h-8 w-44" aria-label={t("insights.costs.platform.window")}>
                <CalendarRange className="text-muted-foreground" />
                <SelectValue>{(v: string) => t(`insights.costs.windows.${v as PlatformWindow}`)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {PLATFORM_WINDOWS.map((w) => (
                  <SelectItem key={w} value={w}>
                    {t(`insights.costs.windows.${w}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PlatformStatsView window={search.window ?? "24h"} />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={period}
              onValueChange={(v) =>
                setSearch({
                  period: v === "7d" || v == null ? undefined : (v as InsightsPeriod),
                  ...(v !== "custom" ? { from: undefined, to: undefined } : {}),
                })
              }
            >
              <SelectTrigger size="sm" className="h-8 w-44" aria-label={t("insights.costs.filters.period")}>
                <CalendarRange className="text-muted-foreground" />
                <SelectValue>{(v: string) => t(`insights.costs.periods.${v as InsightsPeriod}`)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {INSIGHTS_PERIODS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {t(`insights.costs.periods.${p}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {period === "custom" && (
              <div className="flex items-center gap-1.5">
                <Input
                  type="date"
                  aria-label={t("insights.costs.filters.from")}
                  value={search.from ?? ""}
                  max={search.to}
                  onChange={(e) => setSearch({ from: e.target.value || undefined })}
                  className="h-8 w-36 text-xs"
                />
                <ArrowRight className="text-muted-foreground size-3.5" aria-hidden />
                <Input
                  type="date"
                  aria-label={t("insights.costs.filters.to")}
                  value={search.to ?? ""}
                  min={search.from}
                  onChange={(e) => setSearch({ to: e.target.value || undefined })}
                  className="h-8 w-36 text-xs"
                />
              </div>
            )}
            <Select
              value={search.agent != null ? String(search.agent) : "all"}
              onValueChange={(v) => setSearch({ agent: v === "all" || v == null ? undefined : Number(v) })}
            >
              <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("insights.runs.columns.agent")}>
                <SelectValue>
                  {(v: string) =>
                    v === "all" ? t("insights.costs.filters.allAgents") : agentName(Number(v))
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("insights.costs.filters.allAgents")}</SelectItem>
                {agents.data?.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={search.model != null ? String(search.model) : "all"}
              onValueChange={(v) => setSearch({ model: v === "all" || v == null ? undefined : Number(v) })}
            >
              <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("insights.runs.columns.model")}>
                <SelectValue>
                  {(v: string) =>
                    v === "all" ? t("insights.costs.filters.allModels") : modelName(Number(v))
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("insights.costs.filters.allModels")}</SelectItem>
                {models.data?.map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={search.source ?? "all"}
              onValueChange={(v) => setSearch({ source: v === "all" || v == null ? undefined : v })}
            >
              <SelectTrigger size="sm" className="h-8 w-36" aria-label={t("insights.runs.columns.source")}>
                <SelectValue>
                  {(v: string) => (v === "all" ? t("insights.costs.filters.allSources") : sourceLabel(v))}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("insights.costs.filters.allSources")}</SelectItem>
                {[...RUN_SOURCES, "monitor"].map((s) => (
                  <SelectItem key={s} value={s}>
                    {sourceLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {search.user != null && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSearch({ user: undefined })}
                aria-label={t("insights.costs.filters.removeUser")}
              >
                {t("insights.costs.filters.user", {
                  name: userLabels.get(search.user) ?? `#${search.user}`,
                })}
                <X />
              </Button>
            )}
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setSearch({ agent: undefined, model: undefined, user: undefined, source: undefined })
                }
              >
                {t("insights.costs.filters.clear")}
              </Button>
            )}
          </div>

          {customValid ? (
            <TenantCosts
              filters={filters}
              dimension={dimension}
              onDimension={(d) => setSearch({ dim: d === "agent" ? undefined : d })}
              onDrill={drill}
            />
          ) : (
            <EmptyState
              icon={CalendarRange}
              title={t("insights.costs.filters.pickRange")}
              description={t("insights.costs.filters.pickRangeHint")}
            />
          )}
        </div>
      )}
    </PageContainer>
  )
}
